/* eslint-disable max-len */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import './PatientTray.css'
import { useAppSelector, useAppDispatch } from '@store/hooks'
import {
  setSelectedPatient,
  removeSelectedPatient,
  clearAllSelectedPatients,
  restorePatientSelection,
} from '@store/reducers/patientmanagement.reducer'
import {
  setViewProvider,
  removeSelectedProvider,
  clearSelectedProviders,
  restoreProviderSelection,
} from '@store/reducers/providermanagement.reducer'
import {
  toggleSidePanel,
  setActiveSidePanel,
  closeSidePanel,
} from '@store/reducers/uiSlice.reducer'
import { TooltipComponent } from '@syncfusion/ej2-react-popups'
import { Patient } from '@dataTypes/PatientInfo.types'
import { IContact } from '@dataTypes/contact.type'
import { Provider } from '@dataTypes/provider.types'
import { Avatar } from '@components/common'
import { getFullName } from '@utils/fullname.util'
import ActiveCallModal from './ActiveCallModal/ActiveCallModal.component'
import SelectPatientModal from '@components/layout/Modals/SelectPatient/SelectPatient.component'
import { SET_INPUT_NUMBER } from '@store/reducers/messageCenter.reducer'
import {
  buildPatientProfilePath,
  navigateToPatientProfilePreservingReturn,
} from '@utils/patientProfileNavigation.util'
import {
  PATIENT_SELECTION_STORAGE_KEY_PREFIX,
  PATIENT_TRAY_UI_STORAGE_KEY_PREFIX,
  PROVIDER_SELECTION_STORAGE_KEY_PREFIX,
  hasPersistedPatientSelection,
  type PersistedPatientSelection,
} from '@utils/patientSelectionStorage.util'

const PROVIDER_MANAGEMENT_PATH = '/provider-management'
const PATIENT_PROFILE_PATH_PATTERN = /^\/patient-profile\/[^/]+/
// Routes that require an active patient; the select-patient modal auto-opens here.
const SELECT_PATIENT_ROUTE_PATTERNS: RegExp[] = [
  /^\/charting(\/|$)/,
  /^\/notes(\/|$)/,
  /^\/insurance(\/|$)/,
  /^\/image-viewer(\/|$)/,
  /^\/image-xrayviewer(\/|$)/,
  /^\/files(\/|$)/,
]

/** Top-level segment for patient-required areas (e.g. charting), ignoring sub-tabs. */
const getPatientSelectionRouteFamily = (pathname: string): string | null => {
  if (
    !SELECT_PATIENT_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname))
  ) {
    return null
  }
  return pathname.split('/').filter(Boolean)[0] ?? null
}
type PersistedProviderSelection = {
  viewProviderId: string | null
  selectedProviderIds: string[]
  isProviderSidePanelOpen: boolean
}

type PersistedPatientTrayUI = {
  showPatients: boolean
  showProviders: boolean
}

function getInitialTrayUI(
  clinicId: string | undefined,
): PersistedPatientTrayUI {
  if (!clinicId) return { showPatients: false, showProviders: false }
  try {
    const raw = sessionStorage.getItem(
      `${PATIENT_TRAY_UI_STORAGE_KEY_PREFIX}${clinicId}`,
    )
    if (!raw) return { showPatients: false, showProviders: false }
    const parsed = JSON.parse(raw) as PersistedPatientTrayUI
    return {
      showPatients: Boolean(parsed.showPatients),
      showProviders: Boolean(parsed.showProviders),
    }
  } catch {
    return { showPatients: false, showProviders: false }
  }
}

const PatientTray: React.FC = (): React.ReactElement => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const isProviderPage = location.pathname === PROVIDER_MANAGEMENT_PATH
  const isPatientProfilePage = PATIENT_PROFILE_PATH_PATTERN.test(
    location.pathname,
  )
  const callerInfo = useAppSelector((state) => state.calls.callerInfo)
  const currentCall = useAppSelector((state) => state.calls.currentCall)
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const clinicId = selectedClinic?.clinic_id
  const [showPatients, setShowPatients] = useState(
    () => getInitialTrayUI(clinicId).showPatients,
  )
  const [showProviders, setShowProviders] = useState(
    () => getInitialTrayUI(clinicId).showProviders,
  )
  const [isSelectPatientDismissed, setIsSelectPatientDismissed] =
    useState(false)
  const [hasAttemptedPatientRehydration, setHasAttemptedPatientRehydration] =
    useState(false)
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const patientListLoading = useAppSelector((state) => state.patient.loading)
  const hasLoadedPatients = useAppSelector(
    (state) => state.patient.hasLoadedPatients,
  )
  const selectedPatients = useAppSelector(
    (state) => state.patient.selectedPatients,
  )
  const selectedProviders = useAppSelector(
    (state) => state.provider.selectedProviders,
  )
  const viewProvider = useAppSelector((state) => state.provider.viewProvider)
  const { isSidePanelOpen, activeSidePanel, patientSelectionRequestId } =
    useAppSelector((state) => state.ui)

  const prevClinicIdRef = useRef<string | undefined>(undefined)
  const hasRehydratedPatientsRef = useRef(false)
  const hasRehydratedProvidersRef = useRef(false)

  // Restore showPatients / showProviders from sessionStorage when clinic is set or changes
  useEffect(() => {
    if (!clinicId) return
    const ui = getInitialTrayUI(clinicId)
    setShowPatients(ui.showPatients)
    setShowProviders(ui.showProviders)
  }, [clinicId])

  // Automatically show the patients or providers section when there is selection (e.g. after viewing a patient/provider or after rehydration)
  useEffect(() => {
    if (selectedPatients.length > 0) {
      setShowPatients(true)
    }
  }, [selectedPatients.length, selectedPatient?.patient_id])
  useEffect(() => {
    if (
      isProviderPage &&
      (selectedProviders.length > 0 || viewProvider?.provider_id != null)
    ) {
      setShowProviders(true)
    }
  }, [isProviderPage, selectedProviders.length, viewProvider?.provider_id])

  // Persist tray UI (show patients / show providers) so it survives refresh
  useEffect(() => {
    if (!clinicId) return
    const key = `${PATIENT_TRAY_UI_STORAGE_KEY_PREFIX}${clinicId}`
    try {
      sessionStorage.setItem(
        key,
        JSON.stringify({
          showPatients,
          showProviders,
        } satisfies PersistedPatientTrayUI),
      )
    } catch {
      // ignore storage errors
    }
  }, [clinicId, showPatients, showProviders])

  // Persist patient selection so it survives refresh; clear only after rehydration
  const selectedPatientIds = useMemo(
    () => selectedPatients.map((p) => p.patient_id),
    [selectedPatients],
  )
  useEffect(() => {
    if (!clinicId) return
    const key = `${PATIENT_SELECTION_STORAGE_KEY_PREFIX}${clinicId}`
    const hasSelection =
      selectedPatient?.patient_id != null || selectedPatientIds.length > 0
    try {
      if (!hasSelection) {
        // Avoid wiping stored selection before the rehydrate effect runs
        if (hasRehydratedPatientsRef.current) {
          sessionStorage.removeItem(key)
        }
        return
      }
      const payload: PersistedPatientSelection = {
        selectedPatientId: selectedPatient?.patient_id ?? null,
        selectedPatientIds,
        isPatientSidePanelOpen: isSidePanelOpen && activeSidePanel === 'main',
      }
      sessionStorage.setItem(key, JSON.stringify(payload))
    } catch {
      // ignore storage errors
    }
  }, [clinicId, selectedPatient?.patient_id, selectedPatientIds])

  // Persist provider selection to sessionStorage when on provider page (so tray list survives refresh; same key as ProviderManagement)
  const providerSelectionIds = useMemo(
    () => selectedProviders.map((p) => p.provider_id),
    [selectedProviders],
  )
  useEffect(() => {
    if (!isProviderPage || !clinicId) return
    const hasSelection =
      viewProvider?.provider_id != null || providerSelectionIds.length > 0
    if (!hasSelection) return
    const key = `${PROVIDER_SELECTION_STORAGE_KEY_PREFIX}${clinicId}`
    try {
      const payload: PersistedProviderSelection = {
        viewProviderId: viewProvider?.provider_id ?? null,
        selectedProviderIds: providerSelectionIds,
        isProviderSidePanelOpen: isSidePanelOpen && activeSidePanel === 'main',
      }
      sessionStorage.setItem(key, JSON.stringify(payload))
    } catch {
      // ignore storage errors
    }
  }, [
    isProviderPage,
    clinicId,
    viewProvider?.provider_id,
    providerSelectionIds,
    isSidePanelOpen,
  ])

  // Rehydrate provider selection from sessionStorage when on provider page and provider data has loaded (e.g. after refresh)
  const providerData = useAppSelector((state) => state.provider.data)
  useEffect(() => {
    if (
      !isProviderPage ||
      !clinicId ||
      providerData.length === 0 ||
      hasRehydratedProvidersRef.current
    ) {
      return
    }
    hasRehydratedProvidersRef.current = true
    const key = `${PROVIDER_SELECTION_STORAGE_KEY_PREFIX}${clinicId}`
    try {
      const raw = sessionStorage.getItem(key)
      if (!raw) return
      const saved = JSON.parse(raw) as PersistedProviderSelection
      const providerById = new Map(providerData.map((p) => [p.provider_id, p]))
      const selectedIds = saved.selectedProviderIds ?? []
      const resolvedSelected = selectedIds
        .map((id) => providerById.get(id))
        .filter((p): p is Provider => p != null)
      const viewProviderResolved =
        saved.viewProviderId != null
          ? (providerById.get(saved.viewProviderId) ?? null)
          : null
      if (resolvedSelected.length > 0 || viewProviderResolved) {
        let selectedProvidersToRestore = resolvedSelected
        if (
          viewProviderResolved &&
          !selectedProvidersToRestore.some(
            (p) => p.provider_id === viewProviderResolved.provider_id,
          )
        ) {
          selectedProvidersToRestore = [
            viewProviderResolved,
            ...selectedProvidersToRestore,
          ]
        }
        dispatch(
          restoreProviderSelection({
            viewProvider: viewProviderResolved,
            selectedProviders: selectedProvidersToRestore,
          }),
        )
        if (saved.isProviderSidePanelOpen && viewProviderResolved) {
          dispatch(setActiveSidePanel('main'))
        }
      }
    } catch {
      // ignore parse or storage errors
    }
  }, [isProviderPage, clinicId, providerData, dispatch])

  // Rehydrate patient selection from sessionStorage after patient data has loaded (e.g. after refresh)
  const patientData = useAppSelector((state) => state.patient.data)
  useEffect(() => {
    if (!clinicId) {
      hasRehydratedPatientsRef.current = false
      setHasAttemptedPatientRehydration(false)
      return
    }
    if (hasRehydratedPatientsRef.current) {
      return
    }
    if (!hasLoadedPatients || patientListLoading) {
      return
    }
    hasRehydratedPatientsRef.current = true
    setHasAttemptedPatientRehydration(true)
    if (patientData.length === 0) {
      return
    }
    const key = `${PATIENT_SELECTION_STORAGE_KEY_PREFIX}${clinicId}`
    try {
      const raw = sessionStorage.getItem(key)
      if (!raw) return
      const saved = JSON.parse(raw) as PersistedPatientSelection
      const patientById = new Map(patientData.map((p) => [p.patient_id, p]))
      const selectedIds = saved.selectedPatientIds ?? []
      const resolvedSelected = selectedIds
        .map((id) => patientById.get(id))
        .filter((p): p is Patient => p != null)
      const selectedPatientResolved =
        saved.selectedPatientId != null
          ? (patientById.get(saved.selectedPatientId) ?? null)
          : null
      if (resolvedSelected.length > 0 || selectedPatientResolved) {
        let selectedPatientsToRestore = resolvedSelected
        if (
          selectedPatientResolved &&
          !selectedPatientsToRestore.some(
            (p) => p.patient_id === selectedPatientResolved.patient_id,
          )
        ) {
          selectedPatientsToRestore = [
            selectedPatientResolved,
            ...selectedPatientsToRestore,
          ]
        }
        dispatch(
          restorePatientSelection({
            selectedPatient: selectedPatientResolved,
            selectedPatients: selectedPatientsToRestore,
          }),
        )
        if (
          saved.isPatientSidePanelOpen &&
          selectedPatientResolved &&
          !PATIENT_PROFILE_PATH_PATTERN.test(window.location.pathname)
        ) {
          dispatch(setActiveSidePanel('main'))
        }
      }
    } catch {
      // ignore parse or storage errors
    }
  }, [clinicId, patientData, patientListLoading, hasLoadedPatients, dispatch])

  const displayPatients = selectedPatients

  const handlePatientTrayClick = (
    e: React.MouseEvent<HTMLDivElement>,
    patient: Patient,
  ): void => {
    e.preventDefault()
    const contact: IContact | undefined = undefined

    if (isPatientProfilePage) {
      const nextPath = buildPatientProfilePath(patient.patient_id)
      if (location.pathname === nextPath) {
        return
      }
      dispatch(setSelectedPatient({ patient, contact }))
      dispatch(closeSidePanel())
      navigateToPatientProfilePreservingReturn(
        navigate,
        patient.patient_id,
        location.state,
      )
      return
    }

    if (selectedPatient?.patient_id === patient.patient_id && isSidePanelOpen) {
      dispatch(toggleSidePanel(false))
      return
    }

    dispatch(setActiveSidePanel('main'))
    dispatch(setSelectedPatient({ patient, contact }))
  }

  const handleRemovePatient = (patientId: string): void => {
    dispatch(removeSelectedPatient(patientId))

    if (selectedPatient?.patient_id === patientId) {
      dispatch(toggleSidePanel(false))
    }
  }

  const handleProviderTrayClick = (
    e: React.MouseEvent<HTMLDivElement>,
    provider: Provider,
  ): void => {
    e.preventDefault()
    if (
      viewProvider?.provider_id === provider.provider_id &&
      activeSidePanel === 'main' &&
      isSidePanelOpen
    ) {
      dispatch(closeSidePanel())
      return
    }
    dispatch(setViewProvider(provider))
    dispatch(setActiveSidePanel('main'))
  }

  const handleRemoveProvider = (providerId: string): void => {
    if (viewProvider?.provider_id === providerId) {
      dispatch(closeSidePanel())
    }
    dispatch(removeSelectedProvider(providerId))
  }

  // Only clear patient/provider selection when the user actually switches clinic (by id), not when the clinic object reference changes
  useEffect(() => {
    const prevClinicId = prevClinicIdRef.current
    if (prevClinicId !== undefined && prevClinicId !== clinicId) {
      dispatch(clearAllSelectedPatients())
      dispatch(clearSelectedProviders())
      hasRehydratedPatientsRef.current = false
      hasRehydratedProvidersRef.current = false
      setHasAttemptedPatientRehydration(false)
    }
    prevClinicIdRef.current = clinicId
  }, [clinicId, dispatch])

  // Hold the picker only while a persisted patient may still restore.
  // Otherwise open it immediately so list loading can happen inside the modal.
  const isRestoringPersistedPatient =
    Boolean(clinicId) &&
    !selectedPatient?.patient_id &&
    !hasAttemptedPatientRehydration &&
    hasPersistedPatientSelection(clinicId)

  const requiresPatientSelection = useMemo(
    () =>
      Boolean(clinicId) &&
      !selectedPatient?.patient_id &&
      !isRestoringPersistedPatient &&
      SELECT_PATIENT_ROUTE_PATTERNS.some((pattern) =>
        pattern.test(location.pathname),
      ),
    [
      clinicId,
      selectedPatient?.patient_id,
      isRestoringPersistedPatient,
      location.pathname,
    ],
  )
  const patientSelectionRouteFamily = useMemo(
    () => getPatientSelectionRouteFamily(location.pathname),
    [location.pathname],
  )

  // Re-arm when entering a different patient-required area, clearing the patient,
  // or when a page explicitly requests selection — not on sub-tab changes
  // (e.g. /charting → /charting/perio) after the user dismissed the modal.
  useEffect(() => {
    setIsSelectPatientDismissed(false)
  }, [
    patientSelectionRouteFamily,
    selectedPatient?.patient_id,
    patientSelectionRequestId,
  ])

  const handleSelectPatientFromModal = (
    patient: Patient,
    contact?: IContact,
  ): void => {
    dispatch(setSelectedPatient({ patient, contact }))
    dispatch(setActiveSidePanel('main'))
    dispatch(toggleSidePanel(true))
  }

  const handleSelectPatientModalHide = (): void => {
    setIsSelectPatientDismissed(true)
  }

  return (
    <div className="patient-tray">
      <div className="d-flex flex-column align-items-center patient-tray-inner">
        {!isProviderPage && (
          <>
            <TooltipComponent
              content={showPatients ? 'Hide patients' : 'Show patients'}
              position="LeftCenter"
              tabIndex={0}
            >
              <button
                type="button"
                className={`unstyled-button patient-tray-user-icon ${showPatients ? 'patient-tray-user-icon-active' : ''}`}
                aria-label={showPatients ? 'Hide patients' : 'Show patients'}
                onClick={() => {
                  setShowPatients(!showPatients)
                  if (activeSidePanel === 'main') {
                    dispatch(toggleSidePanel(!showPatients))
                  } else {
                    dispatch(setActiveSidePanel('main'))
                  }

                  if (!selectedPatient) dispatch(toggleSidePanel(false))
                }}
              >
                <i className="mgc_user_3_fill" />
              </button>
            </TooltipComponent>

            {showPatients && (
              <>
                {displayPatients.slice(0, 10).map((patient) => (
                  <div className="patient-img-wrapper" key={patient.patient_id}>
                    <TooltipComponent
                      id="patient-tray-tooltip"
                      content={`${patient.first_name} ${patient.last_name}`}
                      position="LeftCenter"
                      tabIndex={0}
                    >
                      <div
                        className={`patient-img-container ${patient.patient_id === selectedPatient?.patient_id ? 'selected' : ''}`}
                        onClick={(e) => handlePatientTrayClick(e, patient)}
                      >
                        <Avatar
                          source={
                            patient?.photo_file?.file_path ??
                            patient?.photo ??
                            'https://cdn-icons-png.flaticon.com/128/9131/9131478.png'
                          }
                          alt="photo"
                          style={{ borderRadius: '6px' }}
                        />
                      </div>
                      <button
                        className="remove-btn"
                        onClick={() => handleRemovePatient(patient.patient_id)}
                        title="Remove"
                      >
                        ×
                      </button>
                    </TooltipComponent>
                  </div>
                ))}

                <button
                  type="button"
                  className="unstyled-button patient-tray-add-icon"
                  aria-label="Add patient"
                  onClick={() => {
                    const input = document.querySelector(
                      '.header-search.input-field',
                    ) as HTMLElement
                    if (input instanceof HTMLInputElement) {
                      input.focus()
                    }
                  }}
                >
                  <i className="mgc_add_line" />
                </button>
              </>
            )}
          </>
        )}

        {isProviderPage && (
          <>
            <TooltipComponent
              content={showProviders ? 'Hide providers' : 'Show providers'}
              position="LeftCenter"
              tabIndex={0}
            >
              <button
                type="button"
                className={`unstyled-button provider-tray-user-icon ${showProviders ? 'provider-tray-user-icon-active' : ''}`}
                aria-label={showProviders ? 'Hide providers' : 'Show providers'}
                onClick={() => {
                  setShowProviders(!showProviders)
                  if (activeSidePanel === 'main') {
                    dispatch(toggleSidePanel(!showProviders))
                  } else {
                    dispatch(setActiveSidePanel('main'))
                  }
                  if (!viewProvider) dispatch(toggleSidePanel(false))
                }}
              >
                <i className="mgc_stethoscope_fill" />
              </button>
            </TooltipComponent>

            {showProviders && (
              <>
                {selectedProviders.slice(0, 10).map((provider) => (
                  <div
                    className="patient-img-wrapper provider-img-wrapper"
                    key={provider.provider_id}
                  >
                    <TooltipComponent
                      id="provider-tray-tooltip"
                      content={getFullName(provider)}
                      position="LeftCenter"
                      tabIndex={0}
                    >
                      <div
                        className={`patient-img-container ${provider.provider_id === viewProvider?.provider_id ? 'selected' : ''}`}
                        onClick={(e) => handleProviderTrayClick(e, provider)}
                      >
                        <Avatar
                          source={
                            provider.photo_file?.file_path ??
                            provider.photo ??
                            'https://cdn-icons-png.flaticon.com/128/9131/9131478.png'
                          }
                          alt={getFullName(provider)}
                          style={{ borderRadius: '6px' }}
                        />
                      </div>
                      <button
                        className="remove-btn"
                        onClick={() =>
                          handleRemoveProvider(provider.provider_id)
                        }
                        title="Remove"
                      >
                        ×
                      </button>
                    </TooltipComponent>
                  </div>
                ))}
              </>
            )}
          </>
        )}

        <TooltipComponent
          content="AI assistant"
          position="LeftCenter"
          tabIndex={0}
        >
          <button
            type="button"
            className={`unstyled-button patient-tray-icon ${activeSidePanel === 'ai' && isSidePanelOpen ? 'patient-tray-icon-active' : ''}`}
            aria-label="AI assistant"
            onClick={() => {
              setShowPatients(false)
              setShowProviders(false)
              if (activeSidePanel === 'ai') {
                dispatch(toggleSidePanel())
              } else {
                dispatch(setActiveSidePanel('ai'))
              }
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_tooth_ai)">
                <path
                  d="M5.88669 3.40039C6.40191 3.40039 6.84141 3.65738 7.1445 3.83496C7.40111 3.98642 7.68853 4.18356 7.99899 4.18359C8.30949 4.18359 8.59685 3.98591 8.85349 3.83496C8.91569 3.79868 8.98353 3.7589 9.05661 3.71875C9.1354 3.92205 9.29696 4.0972 9.54391 4.18164L10.4931 4.50586L10.5791 4.53809C10.7919 4.62484 10.9838 4.75664 11.1416 4.92383C11.2994 5.09105 11.4198 5.29022 11.4941 5.50781L11.8183 6.45605L11.8427 6.52051C12.0142 6.91826 12.4348 7.06681 12.7949 6.96973C12.7925 7.0616 12.7906 7.15352 12.7851 7.24512C12.7297 8.17224 12.5204 9.14233 12.2558 9.98535C11.9929 10.8213 11.6626 11.5716 11.3427 12.0469C10.9752 12.5917 10.4641 12.8633 9.92282 12.7881C9.42621 12.719 9.03989 12.3739 8.84665 11.9912C8.64022 11.5828 8.54529 11.1264 8.36423 10.707C8.27446 10.5002 8.18765 10.3539 8.10739 10.2656C8.023 10.1739 7.97186 10.1764 7.89059 10.2656C7.80985 10.3539 7.72347 10.5003 7.63376 10.707C7.45272 11.1264 7.35776 11.5829 7.15134 11.9912C6.95747 12.3739 6.57133 12.7192 6.07516 12.7881C5.53349 12.8632 5.0227 12.5916 4.65524 12.0469C4.33535 11.5716 4.00505 10.8213 3.74216 9.98535C3.47761 9.14236 3.26876 8.17266 3.21384 7.24609C3.15892 6.33203 3.24846 5.3942 3.6445 4.66504C3.84674 4.29211 4.13297 3.9694 4.5195 3.74219C4.90651 3.51501 5.36453 3.40042 5.88669 3.40039Z"
                  fill="#185DD8"
                />
                <path
                  d="M12.1136 1.0393C12.2938 0.511815 13.0226 0.49584 13.2363 0.991378L13.2544 1.03961L13.4976 1.75096C13.5534 1.9141 13.6435 2.06339 13.7618 2.18875C13.8801 2.31412 14.024 2.41265 14.1836 2.47769L14.249 2.50211L14.9604 2.74505C15.4878 2.9253 15.5038 3.65414 15.0086 3.86785L14.9604 3.88594L14.249 4.12918C14.0858 4.18489 13.9365 4.27496 13.8111 4.39331C13.6857 4.51166 13.5871 4.65553 13.522 4.81522L13.4976 4.88033L13.2547 5.59199C13.0745 6.11948 12.3457 6.13545 12.1323 5.64021L12.1136 5.59199L11.8706 4.88063C11.8149 4.71743 11.7249 4.56809 11.6065 4.44267C11.4882 4.31725 11.3443 4.21868 11.1846 4.1536L11.1195 4.12918L10.4082 3.88624C9.88046 3.70599 9.86448 2.97715 10.36 2.76374L10.4082 2.74505L11.1195 2.50211C11.2827 2.44636 11.4319 2.35628 11.5573 2.23793C11.6827 2.11959 11.7812 1.97573 11.8462 1.81607L11.8706 1.75096L12.1136 1.0393ZM15.0954 5.44632e-08C15.1518 -7.11272e-08 15.207 0.0158181 15.2549 0.0456569C15.3027 0.0754957 15.3412 0.118158 15.366 0.168797L15.3805 0.204063L15.486 0.513322L15.7956 0.61882C15.8521 0.638019 15.9016 0.673563 15.9379 0.720946C15.9742 0.76833 15.9956 0.82542 15.9994 0.884982C16.0032 0.944544 15.9892 1.0039 15.9593 1.05552C15.9293 1.10714 15.8847 1.1487 15.8311 1.17494L15.7956 1.18941L15.4863 1.29491L15.3808 1.60447C15.3616 1.66097 15.326 1.71048 15.2786 1.74674C15.2312 1.783 15.1741 1.80437 15.1146 1.80815C15.055 1.81193 14.9957 1.79794 14.9441 1.76796C14.8925 1.73797 14.8509 1.69335 14.8247 1.63974L14.8103 1.60447L14.7048 1.29521L14.3952 1.18971C14.3387 1.17051 14.2892 1.13497 14.2529 1.08759C14.2166 1.0402 14.1952 0.983114 14.1914 0.923552C14.1876 0.86399 14.2015 0.804638 14.2315 0.753017C14.2614 0.701397 14.3061 0.659832 14.3597 0.63359L14.3952 0.619121L14.7045 0.513624L14.81 0.204063C14.8303 0.14451 14.8687 0.0928105 14.9199 0.0562149C14.9711 0.0196193 15.0325 -3.77626e-05 15.0954 5.44632e-08Z"
                  fill="#185DD8"
                />
              </g>
              <defs>
                <clipPath id="clip0_tooth_ai">
                  <rect width="16" height="16" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </button>
        </TooltipComponent>

        <TooltipComponent content="Chat" position="LeftCenter" tabIndex={0}>
          <button
            type="button"
            className={`unstyled-button patient-tray-icon ${activeSidePanel === 'chat' && isSidePanelOpen ? 'patient-tray-icon-active' : ''}`}
            aria-label="Chat"
            onClick={() => {
              setShowPatients(false)
              setShowProviders(false)
              if (activeSidePanel === 'chat') {
                dispatch(toggleSidePanel())
              } else {
                dispatch(setActiveSidePanel('chat'))
              }
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_chat)">
                <path
                  d="M12.6667 6.6665C13.1971 6.6665 13.7058 6.87722 14.0809 7.25229C14.456 7.62736 14.6667 8.13607 14.6667 8.6665V10.6665C14.6667 11.1969 14.456 11.7056 14.0809 12.0807C13.7058 12.4558 13.1971 12.6665 12.6667 12.6665V13.3105C12.6667 14.0172 11.8427 14.4032 11.3 13.9505L9.75868 12.6665H8.00001C7.46958 12.6665 6.96087 12.4558 6.5858 12.0807C6.21072 11.7056 6.00001 11.1969 6.00001 10.6665V8.6665C6.00001 8.13607 6.21072 7.62736 6.5858 7.25229C6.96087 6.87722 7.46958 6.6665 8.00001 6.6665H12.6667ZM10.6667 2.6665C11.1971 2.6665 11.7058 2.87722 12.0809 3.25229C12.456 3.62736 12.6667 4.13607 12.6667 4.6665V5.33317H7.33334C6.6261 5.33317 5.94782 5.61412 5.44773 6.11422C4.94763 6.61432 4.66668 7.29259 4.66668 7.99984V10.6665C4.66668 11.3625 4.93334 11.9972 5.37068 12.4718L4.66668 12.9998C4.11734 13.4118 3.33334 13.0198 3.33334 12.3332V11.3332C2.80291 11.3332 2.2942 11.1225 1.91913 10.7474C1.54406 10.3723 1.33334 9.8636 1.33334 9.33317V4.6665C1.33334 4.13607 1.54406 3.62736 1.91913 3.25229C2.2942 2.87722 2.80291 2.6665 3.33334 2.6665H10.6667Z"
                  fill="#185DD8"
                />
              </g>
              <defs>
                <clipPath id="clip0_chat">
                  <rect width="16" height="16" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </button>
        </TooltipComponent>

        <TooltipComponent content="Phone" position="LeftCenter" tabIndex={0}>
          <button
            type="button"
            className={`unstyled-button patient-tray-icon ${activeSidePanel === 'phone' && isSidePanelOpen ? 'patient-tray-icon-active' : ''}`}
            aria-label="Phone"
            onClick={() => {
              setShowPatients(false)
              setShowProviders(false)
              if (activeSidePanel === 'phone') {
                dispatch(toggleSidePanel())
              } else {
                dispatch(setActiveSidePanel('phone'))
              }
            }}
          >
            <i className="mgc_phone_fill" />
          </button>
        </TooltipComponent>
      </div>
      {activeSidePanel === 'phone' &&
        !isSidePanelOpen &&
        (currentCall || callerInfo) && (
          <ActiveCallModal
            show={true}
            onHide={() => {
              dispatch(SET_INPUT_NUMBER(''))
            }}
          />
        )}
      {requiresPatientSelection && (
        <SelectPatientModal
          isOpen={requiresPatientSelection && !isSelectPatientDismissed}
          onSelectPatient={handleSelectPatientFromModal}
          onHide={handleSelectPatientModalHide}
        />
      )}
    </div>
  )
}

export default PatientTray
